// Pricing plans displayed on /pricing and reiterated on /checkout/:plan.
// All three plans bundle the same features; the only difference is the
// monthly equivalent price (paid up-front for quarterly/annual tiers).

export type PlanId = 'monthly' | 'quarterly' | 'annual';

export interface Plan {
  id: PlanId;
  name: string;
  /** Headline price displayed prominently. */
  monthlyEquivalent: string;     // e.g. "$29.99"
  /** Per-day shorthand for the loss-aversion line ("only $X/day"). */
  perDay: string;                // e.g. "$0.99"
  /** Total billed at checkout. */
  billedTotal: string;           // e.g. "$29.99/month" or "$59.97 every 3 months" or "$119.88/year"
  /** Numeric USD value charged at checkout — used for GTM `purchase` event `value`. */
  priceUsd: number;
  cadence: string;               // "/ month"
  /** Optional badge (MOST POPULAR, BEST VALUE). */
  badge: string | null;
  /** Optional savings line vs monthly. */
  savings: string | null;
  blurb: string;
}

export const PLANS: Plan[] = [
  {
    id: 'monthly',
    name: 'Monthly',
    monthlyEquivalent: '$29.99',
    perDay: '$0.99',
    billedTotal: '$29.99 billed monthly',
    priceUsd: 29.99,
    cadence: '/ month',
    badge: null,
    savings: null,
    blurb: 'Cancel anytime. No commitment.',
  },
  {
    id: 'quarterly',
    name: 'Quarterly',
    monthlyEquivalent: '$19.99',
    perDay: '$0.66',
    billedTotal: '$59.97 billed every 3 months',
    priceUsd: 59.97,
    cadence: '/ month',
    badge: 'MOST POPULAR',
    savings: 'Save 33% vs monthly',
    blurb: 'For people genuinely making a move.',
  },
  {
    id: 'annual',
    name: 'Annual',
    monthlyEquivalent: '$9.99',
    perDay: '$0.33',
    billedTotal: '$119.88 billed annually',
    priceUsd: 119.88,
    cadence: '/ month',
    badge: 'BEST VALUE',
    savings: 'Save 67% vs monthly',
    blurb: 'Best value. Lock in the year.',
  },
];

// Shared feature list — every plan includes everything below. Ordered from
// most-immediate ("here's your report") to most-aspirational ("ongoing
// support") so the eye lands on what they get TODAY first.
//
// Group coaching is called out explicitly so the "2 hrs of coaching for
// $9.99/mo" math doesn't read as too-good-to-be-true.
export interface PlanFeature {
  /** Short bold-eligible label. */
  label: string;
  /** Optional clarifying line shown below the label. */
  detail?: string;
}

export const PLAN_FEATURES: PlanFeature[] = [
  { label: 'Your top 10 career matches', detail: 'with salary tiers and detailed fit reasons' },
  { label: 'Personality breakdown & deep insights', detail: 'understand your strengths, growth areas, and work style' },
  { label: 'Tailored career strategy', detail: '90-day action plan built around your specific quiz answers' },
  { label: 'Resume & LinkedIn optimization', detail: 'rewrite suggestions matched to your target roles' },
  { label: 'Personalized job board', detail: 'live openings filtered to your top matches' },
  { label: 'Job opening alerts', detail: 'get notified when roles that fit your profile post' },
  { label: 'Unlimited quiz retakes', detail: 'your strengths evolve — your report can too' },
  {
    label: 'Weekly live career coaching',
    detail: 'Up to 2 hrs/month in small-group sessions led by experienced career coaches. Topics: resume reviews, interview prep, salary negotiation, career pivots, and more.',
  },
];

// Trimmed list shown on the /checkout/:plan sidebar. Drops the two
// passive/utility items (job board + alerts) so the sidebar reads as
// "what you're getting today" not a kitchen sink. The full PLAN_FEATURES
// list still appears on /pricing for marketing.
export const CHECKOUT_FEATURES: PlanFeature[] = PLAN_FEATURES.filter(
  (f) =>
    f.label !== 'Personalized job board' &&
    f.label !== 'Job opening alerts',
);
